import { AuthUser } from 'src/app/core/auth/auth.interface';
import {
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
  BooleanOrNullOrUndefined
} from 'src/app/shared/utils';

export type LocationElement = {
  id: number;
  title: string;
  position_id?: string; // Location-specific position identifier
  contract_type?: string;
  salary?: number;
  telehealth_salary?: number;
  fixed_hours_salary?: number;
  fixed_weekly_hours?: number;
  supervision_payment?: string;
  fixed_biweekly_pay?: number;
  fixed_weekly_pay?: number;
  //Professional data
  driver_license_expiration?: string;
  bacb_license_expiration?: string;
  cpr_every_2_years?: string;
  background_every_5_years?: string;
  liability_insurance_annually?: string;
  local_police_rec_every_5_years?: string;
  wc?: string;
  medicaid_provider_expiration?: string;
  national_sex_offender_registry?: string;
  employment_auth_card_expiration?: string;
  auth_permanent_res_expiration?: string;
  e_verify?: string;
  bacb_credential_number?: string;
  //CEU data
  ceu_hippa_annually?: string;
  ceu_security_awareness_annually?: string;
  ceu_zero_tolerance_every_3_years?: string;
  ceu_domestic_violence_no_expiration?: string;
  ceu_hiv_bloodborne_pathogens_infection_control_no_expiration?: string;
  ceu_civil_rights_no_expiration?: string;
  abuse_neglect_and_exploitation?: string;
  required_fields?: string[];
  // Custom (per-location) field values, keyed by field key
  custom_field_values?: Record<string, string | null>;
  // Others (moved from user-level)
  school_badge?: string;
  w_9_w_4_form?: string;
  contract?: string;
  two_four_week_notice_agreement?: string;
  credentialing_package_bcbas_only?: string;
  caqh_bcbas_only?: string;
  reporting_hours_ack?: boolean;
  out_of_projection_settings?: {
    warnings_enabled: boolean;
    reports_enabled: boolean;
  };
  status?: string;
  llc?: string;
  ien?: string;
  city?: string;
  languages?: string;
  ss_number?: string;
  date_of_hire?: string;
  medicaid_provider_identifier?: string;
  schedule?: string;
  npi?: string;
  taxonomy?: string;
  note?: string;
  link_documentation?: string;
};

export interface LocationPosition {
  location_id: number;
  position_id?: string;
  salary?: number;
  telehealth_salary?: number;
  fixed_hours_salary?: number;
  fixed_weekly_hours?: number;
  supervision_payment?: string;
  fixed_biweekly_pay?: number;
  fixed_weekly_pay?: number;
  contract_type?: string;
  required_fields?: string[];
}

export interface LocationProfessionalData {
  location_id: number;
  driver_license_expiration?: string;
  bacb_license_expiration?: string;
  cpr_every_2_years?: string;
  background_every_5_years?: string;
  liability_insurance_annually?: string;
  local_police_rec_every_5_years?: string;
  wc?: string;
  medicaid_provider_expiration?: string;
  national_sex_offender_registry?: string;
  employment_auth_card_expiration?: string;
  auth_permanent_res_expiration?: string;
  e_verify?: string;
  bacb_credential_number?: string;
}

export interface LocationCeuData {
  location_id: number;
  ceu_hippa_annually?: string;
  ceu_security_awareness_annually?: string;
  ceu_zero_tolerance_every_3_years?: string;
  ceu_domestic_violence_no_expiration?: string;
  ceu_hiv_bloodborne_pathogens_infection_control_no_expiration?: string;
  ceu_civil_rights_no_expiration?: string;
  abuse_neglect_and_exploitation?: string;
}

/**
 * Permission type
 *
 * Permissions are now dynamically loaded from the backend API.
 * This type allows both known permissions (for IDE autocomplete)
 * and any string value (for future permissions added via config).
 *
 * To add new permissions:
 * 1. Edit backend config/permissions.php
 * 2. Run: php artisan permissions:update
 * 3. Frontend will automatically fetch new permissions
 */
export type Permission = string;

export const USER_TYPE_PARENT = 'parent';

export type MinimalV2User = {
  id: number;
  name: string;
  surname?: string;
  avatar?: string;
  email?: string;
};

export class AppUser implements AuthUser {
  // Basic
  id: number = 0;
  name: string = '';
  surname?: string;
  full_name?: string;
  status: string = '';
  global_status?: string = '';
  phone?: string;
  email: string = '';
  birth_date?: string;
  gender?: string;
  address?: string;
  roles: string[] = [];
  global_roles?: string[];
  permissions: Permission[] = [];
  locations: LocationElement[] = [];
  location_id: number = 0;
  location_phone_1?: string = ''; // Parents
  location_phone_2?: string = ''; // Parents
  is_admin_view?: boolean;
  available_for_notification?: boolean;
  type?: string; // "user", "vendor"
  avatar?: string;

  // Professional data
  electronic_signature?: string;
  llc?: string;
  ien?: string;
  wc?: string;
  city?: string;
  agency_location?: string;
  languages?: string;
  ss_number?: string;
  date_of_hire?: string;
  start_pay?: string;
  certificate_number?: string;
  npi?: string;
  medicaid_provider_identifier?: string;
  link_documentation?: string;
  schedule?: string;
  taxonomy?: string;
  note?: string;
  contract_type?: string;
  salary?: number;
  telehealth_salary?: number;
  position_id?: string;
  user_type?: string; // "employee", "parent"

  constructor(user: Partial<AuthUser>) {
    Object.assign(this, user);
    // Basic
    this.id = NumberOrNullOrUndefined(this.id) ?? 0;
    this.name = StringOrNullOrUndefined(this.name) ?? '';
    this.surname = StringOrNullOrUndefined(this.surname);
    this.status = StringOrNullOrUndefined(this.status) ?? '';
    this.global_status = StringOrNullOrUndefined(this.global_status) ?? this.status;
    this.email = StringOrNullOrUndefined(this.email) ?? '';
    this.phone = StringOrNullOrUndefined(this.phone);
    this.gender = StringOrNullOrUndefined(this.gender);
    this.address = StringOrNullOrUndefined(this.address);
    this.type = StringOrNullOrUndefined(this.type);
    this.user_type = StringOrNullOrUndefined(this.type);
    this.roles = (this.roles as string[]) ?? [];
    this.global_roles = (this.global_roles as string[]) ?? [];
    this.permissions = (this.permissions as Permission[]) ?? [];
    this.start_pay = StringOrNullOrUndefined(this.start_pay);
    this.certificate_number = StringOrNullOrUndefined(
      this.certificate_number,
    );
    this.locations = ((this.locations as LocationElement[]) ?? []).map(
      (location) => ({
        id: NumberOrNullOrUndefined(location?.id) ?? 0,
        title: StringOrNullOrUndefined(location?.title) ?? '',
        position_id: StringOrNullOrUndefined(location?.position_id),
        contract_type: StringOrNullOrUndefined(location?.contract_type),
        salary: NumberOrNullOrUndefined(location?.salary),
        telehealth_salary: NumberOrNullOrUndefined(location?.telehealth_salary),
        fixed_hours_salary: NumberOrNullOrUndefined(
          location?.fixed_hours_salary,
        ),
        fixed_weekly_hours: NumberOrNullOrUndefined(
          location?.fixed_weekly_hours,
        ),
        supervision_payment: StringOrNullOrUndefined(
          location?.supervision_payment,
        ),
        fixed_biweekly_pay: NumberOrNullOrUndefined(
          location?.fixed_biweekly_pay,
        ),
        fixed_weekly_pay: NumberOrNullOrUndefined(location?.fixed_weekly_pay),
        //Professional data
        driver_license_expiration: StringOrNullOrUndefined(
          location?.driver_license_expiration,
        ),
        bacb_license_expiration: StringOrNullOrUndefined(
          location?.bacb_license_expiration,
        ),
        cpr_every_2_years: StringOrNullOrUndefined(location?.cpr_every_2_years),
        background_every_5_years: StringOrNullOrUndefined(
          location?.background_every_5_years,
        ),
        liability_insurance_annually: StringOrNullOrUndefined(
          location?.liability_insurance_annually,
        ),
        local_police_rec_every_5_years: StringOrNullOrUndefined(
          location?.local_police_rec_every_5_years,
        ),
        wc: StringOrNullOrUndefined(location?.wc),
        medicaid_provider_expiration: StringOrNullOrUndefined(
          location?.medicaid_provider_expiration,
        ),
        national_sex_offender_registry: StringOrNullOrUndefined(
          location?.national_sex_offender_registry,
        ),
        employment_auth_card_expiration: StringOrNullOrUndefined(
          location?.employment_auth_card_expiration,
        ),
        auth_permanent_res_expiration: StringOrNullOrUndefined(
          location?.auth_permanent_res_expiration,
        ),
        e_verify: StringOrNullOrUndefined(location?.e_verify),
        bacb_credential_number: StringOrNullOrUndefined(
          location?.bacb_credential_number,
        ),
        //CEU data
        ceu_hippa_annually: StringOrNullOrUndefined(
          location?.ceu_hippa_annually,
        ),
        ceu_security_awareness_annually: StringOrNullOrUndefined(
          location?.ceu_security_awareness_annually,
        ),
        ceu_zero_tolerance_every_3_years: StringOrNullOrUndefined(
          location?.ceu_zero_tolerance_every_3_years,
        ),
        ceu_domestic_violence_no_expiration: StringOrNullOrUndefined(
          location?.ceu_domestic_violence_no_expiration,
        ),
        ceu_hiv_bloodborne_pathogens_infection_control_no_expiration:
          StringOrNullOrUndefined(
            location?.ceu_hiv_bloodborne_pathogens_infection_control_no_expiration,
          ),
        ceu_civil_rights_no_expiration: StringOrNullOrUndefined(
          location?.ceu_civil_rights_no_expiration,
        ),
        abuse_neglect_and_exploitation: StringOrNullOrUndefined(
          location?.abuse_neglect_and_exploitation,
        ),
        required_fields: (location?.required_fields as string[]) ?? [],
        // Others (moved from user-level)
        school_badge: StringOrNullOrUndefined(location?.school_badge),
        w_9_w_4_form: StringOrNullOrUndefined(location?.w_9_w_4_form),
        contract: StringOrNullOrUndefined(location?.contract),
        two_four_week_notice_agreement: StringOrNullOrUndefined(
          location?.two_four_week_notice_agreement,
        ),
        credentialing_package_bcbas_only: StringOrNullOrUndefined(
          location?.credentialing_package_bcbas_only,
        ),
        caqh_bcbas_only: StringOrNullOrUndefined(location?.caqh_bcbas_only),
        reporting_hours_ack: BooleanOrNullOrUndefined(
          location?.reporting_hours_ack,
        ),
        status: StringOrNullOrUndefined(location?.status),
        llc: StringOrNullOrUndefined(location?.llc),
        ien: StringOrNullOrUndefined(location?.ien),
        city: StringOrNullOrUndefined(location?.city),
        languages: StringOrNullOrUndefined(location?.languages),
        ss_number: StringOrNullOrUndefined(location?.ss_number),
        date_of_hire: StringOrNullOrUndefined(location?.date_of_hire),
        medicaid_provider_identifier: StringOrNullOrUndefined(
          location?.medicaid_provider_identifier
        ),
        schedule: StringOrNullOrUndefined(location?.schedule),
        npi: StringOrNullOrUndefined(location?.npi),
        taxonomy: StringOrNullOrUndefined(location?.taxonomy),
        note: StringOrNullOrUndefined(location?.note),
        link_documentation: StringOrNullOrUndefined(
          location?.link_documentation,
        ),
        custom_field_values: location?.custom_field_values ?? {},
      }),
    );
    this.location_id = NumberOrNullOrUndefined(this.location_id) ?? 0;
    this.is_admin_view = BooleanOrNullOrUndefined(this.is_admin_view);
    this.available_for_notification = BooleanOrNullOrUndefined(this.available_for_notification);
    this.avatar = StringOrNullOrUndefined(this.avatar);
    this.electronic_signature = StringOrNullOrUndefined(this.electronic_signature);
  }
}
